import { ITheme, IStyle, mergeStyleSets, Icon, getTheme } from '@fluentui/react';
import React from 'react';
import { BwjPalette } from '../../../app-core/app-theme';

export interface IRatingBarProps {
  rating: number;
  reviewerCount: number;
  rootStyle?: React.CSSProperties | undefined;
}

const getStyleClasses = (theme: ITheme) => {
  const root: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    gap: '0.55rem',
    alignItems: 'center',
  };
  
  const figure: IStyle = {
    display: 'block',
    color: theme.palette.neutralSecondaryAlt,
    fontSize: theme.fonts.medium.fontSize,
    fontWeight: 700,
    lineHeight: theme.fonts.medium.lineHeight,
  };

  const meterContainer: IStyle = {
    flexGrow: 1,
  };

  const meter: IStyle = {
    width: '100%',
    height: theme.fonts.medium.lineHeight,
    boxSizing: 'border-box',
    border: `1px solid ${theme.palette.neutralSecondaryAlt}`,
    backgroundColor: theme.palette.neutralLighter,

    '& > div': {
      height: '100%',
    },
  };

  const reviewerInfo: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    gap: '0.3rem',
    alignItems: 'center',
  };

  const icon: IStyle = {
    fontSize: theme.fonts.mediumPlus.fontSize,
    color: theme.palette.neutralSecondaryAlt,
  };

  return mergeStyleSets({
    root,
    figure,
    meterContainer,
    meter,
    reviewerInfo,
    icon
  });
}

const getMeterColor = (rating: number) => {
  if(rating < 1.7) { return BwjPalette.themeError; }
  if(rating < 2.6) { return BwjPalette.themeWarningBackgroundDark; }
  if(rating < 3.4) { return '#6f962e'; }
  return BwjPalette.themeSuccess;
}

export const RatingBar: React.FunctionComponent<IRatingBarProps> = (props) => {
  const theme = getTheme();
  const cs = getStyleClasses(theme);
  const normalizedRating = Math.round(props.rating * 10) / 10;
  const rating = normalizedRating.toFixed(1);
  const indicatorLevel = (normalizedRating * 100) / 5;
  const indicatorStyle = {
    width: `${indicatorLevel}%`,
    backgroundColor: getMeterColor(normalizedRating),
  };

  return (
    <div className={cs.root} style={props.rootStyle}>
      <span
        className={cs.figure}
        aria-label={`${rating} rating`}
      >
        {`${rating}/5`}
      </span>
      <div className={cs.meterContainer} aria-hidden={true}>
        <div className={cs.meter}>
          <div style={indicatorStyle} />
        </div>
      </div>
      <div className={cs.reviewerInfo}>
        <Icon
          className={cs.icon}
          iconName='Contact'
          aria-hidden={true}
        />
        <span
          className={cs.figure}
          aria-label={`${props.reviewerCount} reviewers`}
        >
          {props.reviewerCount}
        </span>
      </div>
    </div>
  );
};
